import { pathOr, propOr } from 'ramda'

export const getUserRelationType = (user, bp) => {
  const roles = [
    ...propOr([], 'agents', bp).map(item => ({ id: item.id, role: 'agent' })),
    ...propOr([], 'owners', bp).map(item => ({ id: item.id, role: 'owner' })),
    ...propOr([], 'responsiblePersons', bp).map(item => ({
      id: item.id,
      role: 'responsiblePerson'
    })),
    ...propOr([], 'principalAccountablePersons', bp).map(item => ({
      id: item.id,
      role: 'principalAccountablePerson'
    })),
    ...propOr([], 'principalDesigners', bp).map(item => ({
      id: item.id,
      role: 'principalDesigner'
    })),
    ...propOr([], 'prospectivePurchasers', bp).map(item => ({
      id: item.id,
      role: 'prospectivePurchaser'
    }))
  ]

  const relation = roles.find(role => role.id === user.id)
  return propOr('agent', 'role', relation)
}

export const getUserRemindersPeriod = (user, bp) => {
  const userReminders = propOr([], 'userReminders', bp)
  const periodicReminder = userReminders.find(
    reminder =>
      reminder.notificationType ===
      'App\\Notifications\\BuildingPassport\\PeriodicReminderNotification'
  )
  return propOr(null, 'remindersPeriodInMonths', periodicReminder)
}

export const canEditBp = (user, bp, canFrsEdit = false) => {
  if (user && bp) {
    const relations = [
      ...propOr([], 'owners', bp),
      ...propOr([], 'agents', bp),
      ...propOr([], 'responsiblePersons', bp),
      ...propOr([], 'principalAccountablePersons', bp),
      ...propOr([], 'principalDesigners', bp)
    ].map(r => pathOr('', ['pivot', 'userId'], r))
    return canFrsEdit
      ? user.type === 'emergency_service' ||
          relations.includes(user.id) ||
          bp.creatorId === user.id
      : user.type !== 'emergency_service' &&
          (relations.includes(user.id) || bp.creatorId === user.id)
  }
}

export const isOwner = (user, bp) => {
  if (!user || !bp) return false

  const owners = propOr([], 'owners', bp)
  return owners.some(owner => owner.id === user.id)
}

export const getUserRoleInUnit = (user, bp) => {
  const roles = [
    ...propOr([], 'agents', bp).map(item => ({ id: item.id, role: 'agent' })),
    ...propOr([], 'owners', bp).map(item => ({ id: item.id, role: 'owner' })),
    ...propOr([], 'responsiblePersons', bp).map(item => ({
      id: item.id,
      role: 'responsiblePerson'
    })),
    ...propOr([], 'occupants', bp).map(item => ({
      id: item.id,
      role: 'occupant'
    }))
  ]

  const relations = roles
    .filter(role => role.id === user.id)
    .map(rel => rel.role)

  switch (true) {
    case relations.includes('owner'):
      return 'owner'
    case relations.includes('agent'):
      return 'agent'
    case relations.includes('responsiblePerson'):
      return 'responsiblePerson'
    case relations.includes('occupant'):
      return 'occupant'
    default:
      return null
  }
}

// Building Passport staff users must never appear in customer-facing UI.
// Server filters them out at the API layer; this is the matching client-side
// guard for any path that doesn't go through one of those endpoints.
const STAFF_EMAIL_DOMAINS = ['@artificially.io', '@buildingpassport.com']

export const isStaffEmail = email => {
  if (!email || typeof email !== 'string') return false
  const lower = email.toLowerCase()
  return STAFF_EMAIL_DOMAINS.some(domain => lower.endsWith(domain))
}

export const isStaffUser = user =>
  isStaffEmail(user?.email) || isStaffEmail(user?.user?.email)

export const filterOutStaff = users =>
  Array.isArray(users) ? users.filter(u => !isStaffUser(u)) : users

const EXCLUDED_COMPANY_NAMES = new Set(['building passport', 'buildingpassport'])

export const isBuildingPassportCompany = name => {
  if (!name || typeof name !== 'string') return false
  return EXCLUDED_COMPANY_NAMES.has(name.trim().toLowerCase().replace(/\s+/g, ' '))
}

export const hideEmail = email => {
  if (null !== email && email.includes('@')) {
    const splitted = email.split('@')
    return `${splitted[0][0]}***@${splitted[1]}`
  } else {
    return email
  }
}

export const hidePhoneNumber = phoneNumber => {
  if (!phoneNumber || phoneNumber.length < 3) {
    return phoneNumber
  } else {
    const lastThreeDigits = phoneNumber.slice(-3)
    const stars = '*'.repeat(phoneNumber.length - 3)

    return stars + lastThreeDigits
  }
}

export const getInitials = (firstName, lastName, email) => {
  if (firstName && lastName) return `${firstName[0]}${lastName[0]}`.toUpperCase()
  if (firstName) return firstName.substring(0, 2).toUpperCase()
  if (email) return email.substring(0, 2).toUpperCase()
  return 'U'
}

const AVATAR_COLORS = ['#EF4444', '#F59E0B', '#10B981', '#33becc', '#6366F1', '#8B5CF6', '#EC4899']

export const getAvatarColor = seed => {
  const str = (seed || 'U').toString()
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash)
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length]
}
